import { call, put, takeLatest } from "redux-saga/effects";

import { PayloadAction } from "@reduxjs/toolkit";
import { message } from "antd";
import api from "service/apiService";
import { ApiResponse } from "types";
import { translate } from "utils";
import { PartType } from "../types";
import {
	getPartTypesAsync,
	getPartTypeDetailsAsync,
	GetPartTypesAsyncPayload,
	GetPartTypeDetailsPayload,
	setPartTypes,
	CreatePartTypePayload,
	createNewPartTypeAsync,
	setPartType,
	updatePartTypeAsync,
	UpdatePartTypePayload,
	updatePartTypeStatusAsync,
	UpdatePartTypeStatusPayload,
} from "./partType.slice";
import { COMMON_STATUS } from "constants/constants";

// Get list
function* getPartTypesSaga(action: PayloadAction<GetPartTypesAsyncPayload>) {
	const { query, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<PartType[]>>(
				"/dashboard/car-part-types",
				{ ...query, skip: query.skip, status: query.status === COMMON_STATUS.all.value ? undefined : query.status },
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		yield put({ type: setPartTypes.type, payload: response });
	} else {
		message.error(translate(error.message));
	}
}

function* watchGetPartTypes() {
	yield takeLatest(getPartTypesAsync, getPartTypesSaga);
}

// Get partType details
function* getPartTypeDetailsSaga(action: PayloadAction<GetPartTypeDetailsPayload>) {
	const { id, callback } = action.payload;
	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.get<ApiResponse<PartType>>(
				`/dashboard/car-part-types/${id}`,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		yield put({ type: setPartType.type, payload: response });
		callback?.(response.data);
	} else {
		message.error(translate(error.message));
	}
}

function* watchPartTypeDetails() {
	yield takeLatest(getPartTypeDetailsAsync, getPartTypeDetailsSaga);
}

// Create new partType
function* createNewPartTypeSaga(action: PayloadAction<CreatePartTypePayload>) {
	const { data, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.post("/dashboard/car-part-types", data);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(translate("Create successful"));
		callback();
	} else {
		message.error(translate(error.message));
	}
}

function* watchCreatePartType() {
	yield takeLatest(createNewPartTypeAsync, createNewPartTypeSaga);
}

// Update part type
function* updatePartTypeSaga(action: PayloadAction<UpdatePartTypePayload>) {
	const { data, id, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.patch(
				`/dashboard/car-part-types/${id}`,
				data,
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		callback && callback();
		message.success(translate("Update successful"));
	} else {
		message.error(translate(error.message));
	}
}

function* watchUpdatePartType() {
	yield takeLatest(updatePartTypeAsync, updatePartTypeSaga);
}

// Update part type status
function* updatePartTypeStatusSaga(action: PayloadAction<UpdatePartTypeStatusPayload>) {
	const { status, id, callback } = action.payload;

	const { response, error } = yield call(async () => {
		try {
			const { data: response } = await api.patch(
				`/dashboard/car-part-types/status/${id}`,
				{
					status
				},
			);
			return { response };
		} catch (error) {
			return { error };
		}
	});
	if (response) {
		message.success(translate("Update successful"));
		callback?.();
	} else {
		message.error(translate(error.message));
	}
}

function* watchUpdatePartTypeStatus() {
	yield takeLatest(updatePartTypeStatusAsync, updatePartTypeStatusSaga);
}

const partTypeSaga = [
	watchGetPartTypes(),
	watchPartTypeDetails(),
	watchCreatePartType(),
	watchUpdatePartType(),
	watchUpdatePartTypeStatus()
];
export default partTypeSaga;
